import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  Refresh,
  Link as LinkIcon,
  MouseOutlined,
  TouchApp,
} from '@mui/icons-material';

const UserJourneyAnimation = ({ sessionData, sessionId, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState(new Set([0]));

  useEffect(() => {
    let timer;
    if (isPlaying && activeStep < sessionData.length - 1) {
      timer = setTimeout(() => {
        setActiveStep(prevStep => prevStep + 1);
        setProgress(0);
      }, 3000 / playbackSpeed);

      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + (100 / (3000 / 100)) * playbackSpeed;
        });
      }, 100);

      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    } else if (isPlaying && activeStep === sessionData.length - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, activeStep, sessionData.length, playbackSpeed]);

  const handleNext = () => {
    setActiveStep(prevStep => Math.min(prevStep + 1, sessionData.length - 1));
    setProgress(0);
  };

  const handleBack = () => {
    setActiveStep(prevStep => Math.max(prevStep - 1, 0));
    setProgress(0);
  };

  const handleReset = () => {
    setActiveStep(0);
    setIsPlaying(false);
    setProgress(0);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const changeSpeed = () => {
    setPlaybackSpeed(prev => (prev === 1 ? 2 : prev === 2 ? 4 : 1));
  };

  const getActionIcon = (action) => {
    switch (action.UserActionType) {
      case 'page_view':
        return <LinkIcon />;
      case 'scroll':
        return <MouseOutlined />;
      case 'click':
        return <TouchApp />;
      default:
        return <TouchApp />;
    }
  };

  const getActionColor = (action) => {
    switch (action.UserActionType) {
      case 'page_view':
        return 'primary';
      case 'conversion':
        return 'success';
      case 'scroll':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return {
        domain: urlObj.hostname,
        path: urlObj.pathname,
        search: urlObj.search,
      };
    } catch (e) {
      return { domain: 'unknown', path: url, search: '' };
    }
  };

  const handleStepClick = (index) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  if (!sessionData || sessionData.length === 0) {
    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">No Data Available</Typography>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography>No journey data available for this session.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="primary">Close</Button>
          </DialogActions>
        </Dialog>
    );
  }

  const currentAction = sessionData[activeStep];
  const currentTs = currentAction?.TimeStampDate ?? currentAction?.Timestamp;

  return (
      <Dialog
          open
          onClose={onClose}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: '95vw',
              height: '90vh',
              maxWidth: '1600px',
              maxHeight: '900px',
              display: 'flex',
              flexDirection: 'column',
              m: 2
            }
          }}
      >
        <DialogTitle sx={{ p: 2, pb: 1, flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              User Journey Animation
              <Chip
                  size="small"
                  label={`Step ${activeStep + 1} of ${sessionData.length}`}
                  sx={{ ml: 1 }}
                  color="primary"
                  variant="outlined"
              />
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Button size="small" onClick={changeSpeed} variant="outlined" sx={{ minWidth: '60px' }}>
                {playbackSpeed}x
              </Button>
              <IconButton onClick={onClose} size="small">
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Session ID: {sessionId}
            </Typography>
            {sessionData[0]?.UserIpAddress && (
                <Typography variant="caption" color="text.secondary" display="block">
                  IP Address: {sessionData[0].UserIpAddress}
                </Typography>
            )}
          </Box>
        </DialogTitle>

        <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 3,
              flexShrink: 0,
              '& .MuiLinearProgress-bar': { transition: 'transform 0.1s linear' }
            }}
        />

        <DialogContent
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: 1
            }}
        >
          <Box
              sx={{
                display: 'flex',
                gap: 2,
                height: '100%',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
              }}
          >
            <Box
                sx={{
                  width: { xs: '100%', md: '30%' },
                  flexShrink: 0,
                  display: 'flex'
                }}
            >
              <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: 4,
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    overflow: 'hidden'
                  }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Current Action
                </Typography>

                <Box flex={1} sx={{ overflow: 'auto' }}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Box sx={{ p: 1, borderRadius: 1, backgroundColor: 'primary.main', color: 'white' }}>
                      {getActionIcon(currentAction)}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {currentAction.UserActionType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {moment(currentTs).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                      {currentAction.Href && (
                          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper' }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                              URL
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all', color: 'text.primary' }}>
                              {currentAction.Href}
                            </Typography>
                          </Paper>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Box
                sx={{
                  width: { xs: '100%', md: '70%' },
                  flexShrink: 0,
                  display: 'flex',
                  height: { xs: '50vh', md: 'auto' }
                }}
            >
              <Paper
                  elevation={3}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    maxHeight: { xs: '100%', md: 'none' }
                  }}
              >
                <Box
                    sx={{
                      p: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                >
                  <Typography variant="subtitle2">Journey Timeline</Typography>
                  <Chip size="small" label={`${activeStep + 1} of ${sessionData.length}`} color="primary" variant="outlined" />
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', position: 'relative', px: 2, py: 1 }}>
                  <Box
                      sx={{
                        position: 'absolute',
                        left: 31,
                        top: 0,
                        bottom: 0,
                        width: 2,
                        bgcolor: 'grey.200',
                        zIndex: 0,
                      }}
                  />
                  {sessionData.map((action, index) => {
                    const ts = action?.TimeStampDate ?? action?.Timestamp;
                    return (
                        <Box key={index} sx={{ position: 'relative', pl: 5, pr: 2, py: 1, transition: 'all 0.2s ease' }}>
                          <Box
                              sx={{
                                position: 'absolute',
                                left: 24,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: index === activeStep ? 'primary.main' : 'background.paper',
                                border: 2,
                                borderColor: index === activeStep ? 'primary.main' : 'grey.300',
                                zIndex: 1,
                                transition: 'all 0.2s ease',
                              }}
                          />
                          <Paper
                              elevation={index === activeStep ? 2 : 0}
                              sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                borderRadius: 1,
                                bgcolor: index === activeStep ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
                                transform: index === activeStep ? 'translateX(8px)' : 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: index === activeStep ? 'rgba(25, 118, 210, 0.12)' : 'grey.50',
                                  transform: 'translateX(8px)',
                                },
                              }}
                              onClick={() => handleStepClick(index)}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Box display="flex" alignItems="center" gap={1}>
                                {getActionIcon(action)}
                                <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: index === activeStep ? 600 : 400,
                                      color: index === activeStep ? 'text.primary' : 'text.secondary'
                                    }}
                                >
                                  {action.UserActionType}
                                </Typography>
                              </Box>
                              <Typography
                                  variant="caption"
                                  sx={{
                                    color: index === activeStep ? 'text.primary' : 'text.secondary',
                                    fontWeight: index === activeStep ? 500 : 400
                                  }}
                              >
                                {moment(ts).format('HH:mm:ss')}
                              </Typography>
                            </Box>

                            <Collapse in={expandedSteps.has(index)}>
                              <Box sx={{ mt: 1.5, borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                                {action.Href && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                          color: 'text.secondary',
                                          mb: 1,
                                          wordBreak: 'break-all',
                                          maxWidth: '100%',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}
                                    >
                                      {action.Href}
                                    </Typography>
                                )}
                                <Grid container spacing={2}>
                                  {action.NextWeightedDecile !== undefined && (
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Weighted Decile
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={action.NextWeightedDecile}
                                            color={action.NextWeightedDecile >= 7 ? 'success' : action.NextWeightedDecile >= 4 ? 'warning' : 'default'}
                                            sx={{ mt: 0.5 }}
                                        />
                                      </Grid>
                                  )}
                                  {action.DecileScore !== undefined && (
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Decile Score
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={action.DecileScore}
                                            color={action.DecileScore >= 7 ? 'success' : action.DecileScore >= 4 ? 'warning' : 'default'}
                                            sx={{ mt: 0.5 }}
                                        />
                                      </Grid>
                                  )}
                                  {action.ScrollPosition !== undefined && (
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Scroll Position
                                        </Typography>
                                        <Chip size="small" label={`${action.ScrollPosition}%`} color="info" variant="outlined" sx={{ mt: 0.5 }} />
                                      </Grid>
                                  )}
                                  {action.UserActionScore !== undefined && (
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Action Score
                                        </Typography>
                                        <Chip size="small" label={action.UserActionScore.toFixed(2)} color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                                      </Grid>
                                  )}
                                  {action.PredictedIntent && (
                                      <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Predicted Intent
                                        </Typography>
                                        <Chip size="small" label={action.PredictedIntent} color="secondary" variant="outlined" sx={{ mt: 0.5 }} />
                                      </Grid>
                                  )}
                                  {action.NextUserType && (
                                      <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          User Type
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={action.NextUserType}
                                            color={action.NextUserType === 'high' ? 'success' : action.NextUserType === 'medium' ? 'warning' : 'default'}
                                            variant="outlined"
                                            sx={{ mt: 0.5 }}
                                        />
                                      </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Collapse>
                          </Paper>
                        </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Button onClick={handleReset} startIcon={<Refresh />} size="small" variant="outlined">
            Reset
          </Button>
          <Box sx={{ flex: 1 }} />
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={handleBack} disabled={activeStep === 0} size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <SkipPrevious fontSize="small" />
            </IconButton>
            <IconButton
                onClick={togglePlayPause}
                color="primary"
                size="small"
                sx={{ border: 1, borderColor: 'primary.main', width: 40, height: 40 }}
            >
              {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
            <IconButton
                onClick={handleNext}
                disabled={activeStep === sessionData.length - 1}
                size="small"
                sx={{ border: 1, borderColor: 'divider' }}
            >
              <SkipNext fontSize="small" />
            </IconButton>
          </Box>
        </DialogActions>
      </Dialog>
  );
};

export default UserJourneyAnimation;
